import { ActorSubclass, HttpAgent } from '@icp-sdk/core/agent'
import { Principal } from '@icp-sdk/core/principal'
import { config } from '../appConfig'
import { YUSAN_CANISTER_ID } from '../constants/canisterIds'
import { UNKNOWN_ERROR } from '../constants/swapErrors'
import { approveToken } from '../handlers/walletActions/wallet'
import {
  Action,
  DexPoolInfo,
  DexSwapResult,
  idlFactory as idlYusanFactory,
  TokenType,
  _SERVICE as YusanFactory,
} from '../idls/yusan/yusan.did'
import { ICToken, PlugToken } from '../interfaces/common'
import {
  SwapProvider,
  SwapProviderQuote,
  SwapProviderQuoteFetchError,
  SwapStep,
} from '../interfaces/swapInterface'
import { createActor } from '../utils/createActor'

const YUSAN_ID_KEY = 'yusan_id'

const tokenTypeKey = (t: TokenType): string => Object.keys(t)[0]

class YusanSwapClient {
  public identityActor: ActorSubclass<YusanFactory>
  public anonymousActor: ActorSubclass<YusanFactory>
  private poolsPromise?: Promise<DexPoolInfo[]>

  constructor(identityAgent: HttpAgent) {
    this.identityActor = createActor<YusanFactory>(idlYusanFactory, {
      canisterId: YUSAN_CANISTER_ID,
      agent: identityAgent,
    })
    this.anonymousActor = createActor<YusanFactory>(idlYusanFactory, {
      canisterId: YUSAN_CANISTER_ID,
      agent: config.getAgent(),
    })
  }

  static isSupported(token: PlugToken) {
    return !!token.additionalData[YUSAN_ID_KEY]
  }

  private getPools() {
    if (!this.poolsPromise) {
      this.poolsPromise = this.anonymousActor.dex_pools()
    }
    return this.poolsPromise
  }

  private async findPool(from: TokenType, to: TokenType) {
    const fromKey = tokenTypeKey(from)
    const toKey = tokenTypeKey(to)
    const pools = await this.getPools()
    return pools.find((p) => {
      const t0 = tokenTypeKey(p.token_0)
      const t1 = tokenTypeKey(p.token_1)
      return (t0 === fromKey && t1 === toKey) || (t0 === toKey && t1 === fromKey)
    })
  }

  async getQuote(
    amount: bigint,
    fromToken: PlugToken,
    toToken: PlugToken
  ): Promise<SwapProviderQuote | SwapProviderQuoteFetchError> {
    try {
      const fromType = { [fromToken.additionalData[YUSAN_ID_KEY]]: null } as TokenType
      const toType = { [toToken.additionalData[YUSAN_ID_KEY]]: null } as TokenType

      if (!fromType || !toType) {
        return { error: UNKNOWN_ERROR, provider: SwapProvider.YUSAN }
      }

      const pool = await this.findPool(fromType, toType)
      if (!pool) {
        return { error: UNKNOWN_ERROR, provider: SwapProvider.YUSAN }
      }

      const result = await this.anonymousActor.dex_quote({
        token_in: fromType,
        amount_in: amount,
        pool_id: pool.pool_id,
      })

      if ('Err' in result) {
        return { error: UNKNOWN_ERROR, provider: SwapProvider.YUSAN }
      }

      return {
        price: Number(result.Ok.amount_out),
        priceHuman: Number(result.Ok.amount_out) * 10 ** -toToken.decimals,
        provider: SwapProvider.YUSAN,
        metadata: { poolId: pool.pool_id },
      }
    } catch {
      return { error: UNKNOWN_ERROR, provider: SwapProvider.YUSAN }
    }
  }

  async swap(
    fromToken: ICToken,
    toToken: ICToken,
    amount: bigint,
    minAmountOut: bigint,
    walletPrincipal: string,
    setStep: (step: SwapStep) => void
  ) {
    const fromType = { [fromToken.additionalData[YUSAN_ID_KEY]]: null } as TokenType
    const toType = { [toToken.additionalData[YUSAN_ID_KEY]]: null } as TokenType

    if (!fromType || !toType) {
      return Promise.reject('Yusan does not support this pair')
    }

    const pool = await this.findPool(fromType, toType)
    if (!pool) {
      return Promise.reject('No Yusan pool for this pair')
    }

    // Allowance must cover the transfer_from amount + its ledger fee.
    setStep(SwapStep.Approve)
    await approveToken({
      standard: 'ICRC2',
      tokenCanisterId: fromToken.address,
      spender: { address: YUSAN_CANISTER_ID },
      amount: (amount + fromToken.fee).toString(),
    })

    // Atomic: pull funds into Yusan and execute the swap. With
    // allow_partial_fill: false + fail_fast: true, the BalanceSupply rolls
    // back on swap failure so funds never get stranded inside Yusan.
    setStep(SwapStep.Swap)
    const supplyAndSwap: Action[] = [
      {
        BalanceSupply: {
          recipient: [],
          from_subaccount: [],
          amount,
          token_type: fromType,
        },
      },
      {
        Dex: {
          Swap: {
            min_amount_out: minAmountOut,
            token_in: fromType,
            allow_partial_fill: false,
            amount_in: amount,
            pool_id: pool.pool_id,
          },
        },
      },
    ]
    const result = await this.identityActor.actions({
      actions: supplyAndSwap,
      fail_fast: true,
    })
    if ('Err' in result) {
      return Promise.reject(Object.keys(result.Err)[0])
    }
    const innerError = result.Ok.find((r) => 'Err' in r)
    if (innerError && 'Err' in innerError) {
      throw Error(Object.keys(innerError.Err)[0])
    }

    const swapResponse = result.Ok.find(
      (r): r is { Ok: { DexResponse: { Swap: DexSwapResult } } } =>
        'Ok' in r && 'DexResponse' in r.Ok && 'Swap' in r.Ok.DexResponse
    )
    if (!swapResponse) {
      throw Error('Missing Dex.Swap response')
    }
    const amountOut = swapResponse.Ok.DexResponse.Swap.amount_out

    setStep(SwapStep.Withdraw)
    const withdrawResult = await this.identityActor.actions({
      actions: [
        {
          BalanceWithdraw: {
            to: { owner: Principal.fromText(walletPrincipal), subaccount: [] },
            from_subaccount: [],
            fee_mode: [],
            amount: amountOut,
            token_type: toType,
          },
        },
      ],
      fail_fast: true,
    })
    if ('Err' in withdrawResult) {
      return Promise.reject(Object.keys(withdrawResult.Err)[0])
    }
    const withdrawInnerError = withdrawResult.Ok.find((r) => 'Err' in r)
    if (withdrawInnerError && 'Err' in withdrawInnerError) {
      throw Error(Object.keys(withdrawInnerError.Err)[0])
    }

    return Number(amountOut - toToken.fee)
  }
}

export default YusanSwapClient
